import "./FilterStructures";

import { AppContext } from "App";

import { ConditionParams } from "@multiweb/clients/dataReader";
import { getDataReaderClient } from "@multiweb/clients/util";
import { IPublicAttributeInfo } from "@multiweb/types/contracts";
import { IFormContext } from "@multiweb/types/multiweb";
import { Flags } from "OW.Remoting/Remoting/DataTypes/Flags";
import { NullOWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { IBooleanDetailValueEvent } from "UI/UI.BaseControls/IEvent";
import { DropDownMenuItem } from "UI/UI.Controls/DropDownMenu/DropDownMenuItem";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";
import { CaseInsensitiveMap } from "../../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { Event } from "../../../../../Global/Base/Events";
import { IDisposable } from "../../../../../Global/Base/IDisposable";
import { DateUtils } from "../../../../../Global/Base/Utils/DateUtils";
import { StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { ModelDescriptionsManager } from "../../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { Column } from "../../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { IconColumn } from "../../../../UI.Controls/DataGridSubcontrols/Columns/IconColumn";
import { LovColumn } from "../../../../UI.Controls/DataGridSubcontrols/Columns/LOVColumn";
import { FrameContext } from "../../UIManagerInterfaces";
import { LovColumn as OriginalLovColumn } from "../Frames/ColumnTemplates/LOVColumn";
import { DropDownMenuItemWithButton } from "./DropDownMenuItems/DropDownMenuItemWithButton";
import { FilterBlock } from "./FilterBlock";
import { FilterGroup } from "./FilterGroup";
import { FilterBlockAttrNameToData, FilterBlockAttrNameToDataMap, FilterJsonResultSign, IFilterBlockExecutedEventArgs, IFilterJsonData, IFilterJsonDataAttr } from "./FilterInterfaces";
import { FilterPanel } from "./FilterPanel";
import { FilterRegistrations } from "./FilterRegistrations";
import { FilterManagerColumn, IFilterManager } from "./IFilterManager";
import { logger } from "./logger";

/** Konfigurace pro FilterPanel */
export interface IFilterPanelConfig {
    /** Filter panel */
    filterPanel: FilterPanel;
    /** Slupce */
    columns: Array<FilterManagerColumn>;
    /** Název formuláře */
    formName: string;
    /** Kontext framu */
    frameContext: FrameContext;
    /** Unikátní identifikátor framu */
    frameUniqueIdentifier: string;
    /** Třída */
    className: string;
    /** Podmínka */
    frameCondition: string;
    /** Parametry podmínky */
    frameConditionParams: ConditionParams;
    /** ID aktuálního uživatele */
    currentUserId: number | null;

    /**
     * Vypnutí zkracování hodnot. Nastavením na true se do nabídky možných hodnot načtou nezkrácená data. Použito u filtru Hodnoty jsou/nejsou.
     * Tohle nastavení je přebíráno ze stejně pojmenovaného nastavení na frame.
     */
    disableValueShortening: boolean;

    /**
     * Pokud uživatel má uživatel EDITIMPPROFILE může editovat implementační filtry
     * https://teaf.tescosw.cz/wiki/index.php?title=Syst%C3%A9mov%C3%A9_operace
     */
    canEditImplementationFilters: boolean;
}

/**
 * Třída starající se na daném framu o filtraci, obsahuje seznam vsech filtru daneho framu
 */
export class FilterManager implements IFilterManager, IDisposable {
    /** Událost vyvolaná manažerem při stisku na tlačítko filtrovat */
    public filterExecuted: Event<IFilterManager, IFilterExecutedEventArgs> = new Event<IFilterManager, IFilterExecutedEventArgs>();

    /** Událost vyvolaná při čištění filtrů */
    public filterCleared: Event<IFilterManager, string> = new Event<IFilterManager, string>();

    /** Událost volaná při vytvoření nového filtru */
    public filterCreated: Event<IFilterManager, FilterBlock> = new Event<IFilterManager, FilterBlock>();

    /** Událost volaná při zvolení filtru */
    public filterChanged: Event<IFilterManager, FilterBlock> = new Event<IFilterManager, FilterBlock>();

    /** Událost volaná při uložení filtru */
    public filterSaveStateChanged: Event<IFilterManager, FilterBlock> = new Event<IFilterManager, FilterBlock>();

    /** Událost volaná při změně podmínky na frame */
    public frameConditionChanged: Event<IFilterManager, string> = new Event<IFilterManager, string>();

    /** Událost volaná při změně pokročilého filtru */
    public advancedConditionChanged: Event<IFilterManager, string> = new Event<IFilterManager, string>();

    private _filters: FilterBlock[] = []; // seznam filtru
    private _lastUsedFilter: FilterBlock; // posledni pouzity filtr
    private _filterPanel: FilterPanel; // nadrazeny filterPanel
    // seznam ke třídění
    private _columns: Array<FilterManagerColumn> = [];
    private _formName: string; // název formuláře
    private _frameUniqueIdentifier: string; // unikátní identifikátor framu
    private _canEditImplementationFilters: boolean = false; // zdali má uživatel EDITIMPPROFILE https://teaf.tescosw.cz/wiki/index.php?title=Syst%C3%A9mov%C3%A9_operace
    private _filterSign: FilterJsonResultSign = "and";
    private _frameCondition: string; //Podmínka framu z definice. Slouží k tomu, aby při načítání a vyhledávání hodnot se nevraceli data, které nesplňují podmínku framu.
    private _frameConditionParams: ConditionParams;
    /**
     * Vypnutí zkracování hodnot. Nastavením na true se do nabídky možných hodnot načtou nezkrácená data. Použito u filtru Hodnoty jsou/nejsou.
     * Tohle nastavení je přebíráno ze stejně pojmenovaného nastavení na frame.
     */
    private _disableValueShortening: boolean;

    private _filterText: Promise<string> = Localization.getTextPromise("GUI-28839"); // Filtr
    private _advancedFilterText: Promise<string> = Localization.getTextPromise("GUI-691982"); // Pokročilý filtr

    private _filterBlockExecutedHandler: (sender: FilterBlock, eventArgs: IFilterBlockExecutedEventArgs) => Promise<void>;
    private readonly _currentUserId: number | null;
    /** Kontext framu */
    public frameContext: FrameContext = undefined;
    /** Obsahuje informace o přihlášeném uživateli z důvodu ukládání filtrů */
    public static userLoggedInfo: { userName: string; userID: string };
    public className: string;
    /*** Seznam, obsahující jako key AttrName a hodnotu Datovy typ a lokalizační klíč pro seznam sloupců */
    public attrNameToDataType: FilterBlockAttrNameToDataMap = new CaseInsensitiveMap<FilterBlockAttrNameToData>();

    public get frameName(): string {
        return this._frameUniqueIdentifier;
    }
    public set frameCondition(value: string) {
        this._frameCondition = value;
        this.frameConditionChanged.trigger(this, value);
    }

    /** Aktuálně použitý filtr */
    public get currentFilter(): FilterBlock {
        return this._lastUsedFilter;
    }

    /** Zda je nastaven pokročilý filtr */
    public get hasActiveAdvancedFilter(): boolean {
        return this.currentFilter.hasActiveAdvancedFilter;
    }

    public get canEditImplementationFilters(): boolean {
        return this._canEditImplementationFilters;
    }

    constructor({ filterPanel, className, frameConditionParams, columns, formName, frameUniqueIdentifier, frameCondition, currentUserId, frameContext, disableValueShortening, canEditImplementationFilters: canEditImplementationFilters }: IFilterPanelConfig) {
        this._filterPanel = filterPanel;
        this._columns = columns;

        this._formName = formName;
        this._frameUniqueIdentifier = frameUniqueIdentifier;

        for (const dgc of columns) {
            this.attrNameToDataType.set(dgc.attrName, {
                dataType: dgc.dataType.split("|")[0],
                locKey: dgc.locKey,
                locParams: dgc.locParams,
                canFilterNull: dgc.canFilterNull,
                canFilterCompare: dgc.canFilterCompare,
                canFilterEqual: dgc.canFilterEqual,
                canFilterLike: dgc.canFilterLike,
            });
        }

        this.className = className;
        this._frameCondition = frameCondition;
        this._frameConditionParams = frameConditionParams;
        this._disableValueShortening = disableValueShortening;
        this._currentUserId = currentUserId;
        this.frameContext = frameContext;
        this._canEditImplementationFilters = canEditImplementationFilters;
    }
    public formContext: IFormContext;

    public async init(): Promise<void> {
        await this.loadSavedFilters();
    }

    /**
     * Vrací použitý FilterPanel
     */
    public get filterPanel(): FilterPanel {
        return this._filterPanel;
    }

    /**
     * Hledá sloupec s informacemi pro filtrování.
     * @param attrName Atribut sloupce, který se hledá.
     * @returns Nalezený sloupec, jinak undefined.
     */
    public getFilterColumn(attrName: string): FilterManagerColumn {
        return this._columns.find(c => StringUtils.compareCaseInsensitive(c.attrName, attrName));
    }

    /**
     * Vrátí informace pro filtrovací pole (pro sloupce LOV nebo s nastaveným DisplayAttributName vrátí informace pro skutečně zobrazovaný atribut)
     * @param columns Seznam sloupcu
     * @param objectName Object name
     */
    public static async getColumnListByDataGridColumns(columns: Column[], objectName: string): Promise<Array<FilterManagerColumn>> {
        const filterColumns: Array<FilterManagerColumn> = [];

        // Chyba inicializace gridu, nemá smysl pokračovat.
        if (columns == null) {
            return filterColumns;
        }

        const attrs: string[] = [];
        for (const column of columns) {
            if (column instanceof LovColumn ||
                column instanceof OriginalLovColumn ||
                column.displayAttributeName) {
                attrs.push(column.filterAttrName);
            }
        }

        // Načtení informací o vazebních atributech.
        const attrInfos = new CaseInsensitiveMap<IPublicAttributeInfo>();
        if (attrs.length !== 0) {
            const infos = await ModelDescriptionsManager.instance.getAttributesInfo(objectName, attrs);
            for (const info of infos) {
                attrInfos.set(info.attributeName, info);
            }
        }

        for (const column of columns) {
            const attrInfo = attrInfos.get(column.filterAttrName);
            let dataType = attrInfo?.type ?? column.dataType;
            if (column instanceof IconColumn) {
                dataType = "Icon";
            }

            filterColumns.push({
                attrName: column.filterAttrName,
                locKey: column.locKey,
                locParams: column.locParams,
                dataType: dataType,
                displayFormat: column.displayFormat,
                canFilterNull: attrInfo?.canFilterNull ?? column.canFilterNull,
                canFilterCompare: attrInfo?.canFilterCompare ?? column.canFilterCompare,
                canFilterEqual: attrInfo?.canFilterEqual ?? column.canFilterEqual,
                canFilterLike: attrInfo?.canFilterLike ?? column.canFilterLike,
            });
        }

        await Promise.all(filterColumns.map(async (filterColumn) => {
            const locText = await Localization.getTextPromise(filterColumn.locKey);
            filterColumn.locText = locText ?? "";
        }));
        return filterColumns;
    }

    /**
     * Vrátí aktuálně použitý filtr aktivním průchodem spravovaných filtrů a výběrem použitého
     */
    public getCurrentFilter(): FilterBlock {
        for (const filter of this._filters) {
            if (filter.isUsed) {
                this._lastUsedFilter = filter;
                return filter;
            }
        }

        return null;
    }

    private _getFilterBlockByFilterId(filterId: string): FilterBlock | null {
        return this._filters.find(filterBlock => filterBlock.filterId == filterId) || null;
    }

    /**
     * Vybere filtr
     * @param filter FilterBlock který se má vybrat
     * @param addToFilterPanel (Volitelný) Příznak, zda se má FilterBlock přidat do FilterPanelu
     */
    public selectFilter(filter: FilterBlock, addToFilterPanel = false): void {
        if (this._lastUsedFilter) {
            this._lastUsedFilter.isUsed = false;
        }

        if (filter) {
            filter.isUsed = true;
            this._lastUsedFilter = filter;
            if (this._lastUsedFilter.attrNameToDataType.size != this.attrNameToDataType.size) {
                this._lastUsedFilter.attrNameToDataType = this.attrNameToDataType;
            }

            if (addToFilterPanel) {
                this.updateFilterPanel();
            }

            this.filterPanel.updateFilterButtonState(false);
        } else {
            this.clearFilter();
        }

        this.filterChanged.trigger(this, filter);
    }

    /**
     * Vybere filtr, aktualizuje stav filtrovacího panelu a spustí filtrování
     * @param filter FilterBlock který se má vybrat
     * @param addToFilterPanel (Volitelný) Příznak, zda se má FilterBlock přidat do FilterPanelu
     */
    public selectAndApplyFilter(filterBlock: FilterBlock, addToFilterPanel = true): void {
        this.selectFilter(filterBlock);
        if (addToFilterPanel) {
            this.updateFilterPanel();
        } else {
            this._assignFilterBlockEvents(filterBlock);
        }
        this.execute();
    }

    /**
     * Vybere filtr podle jeho identifikátoru
     * @param filterId Identifikátor filtru, který se má vybrat. Pokud je null, zruší se filtrování
     * @param addToFilterPanel (Volitelný) Příznak, zda se má FilterBlock přidat do FilterPanelu
     * @returns Pokud byl nalezen filtr s daným identifikátorem, vrací se jeho FilterBlock; Jinak null
     */
    public selectFilterById(filterId: string | null, addToFilterPanel = false): FilterBlock | null {
        if (filterId !== null) {
            const filterBlockToSelect = this._getFilterBlockByFilterId(filterId);

            if (filterBlockToSelect) {
                this.selectFilter(filterBlockToSelect, addToFilterPanel);
            }

            return filterBlockToSelect || null;
        }

        this.clearFilter();
        return null;
    }

    /**
     * Vybere filtr podle jeho identifikátoru, aktualizuje stav filtrovacího panelu a spustí filtrování
     * @param filterId Identifikátor filtru, který se má vybrat. Pokud je null, zruší se filtrování
     * @param addToFilterPanel (Volitelný) Příznak, zda se má FilterBlock přidat do FilterPanelu
     * @returns Pokud byl nalezen filtr s daným identifikátorem, vrací se jeho FilterBlock; Jinak null
     */
    public selectAndApplyFilterById(filterId: string | null, addToFilterPanel = true): FilterBlock | null {
        if (filterId !== null) {
            const filterBlockToSelect = this._getFilterBlockByFilterId(filterId);

            if (filterBlockToSelect) {
                this.selectAndApplyFilter(filterBlockToSelect, addToFilterPanel);
            }

            return filterBlockToSelect || null;
        }

        this.clearFilter();
        return null;
    }

    /**
     * Vrátí seznam filtrů
     */
    public async getFilterList(): Promise<Map<string, FilterBlock>> {
        const filterMap = new Map<string, FilterBlock>();

        await this._updateFiltersCaption();

        for (const fb of this._filters) {
            if (!fb.filterName) {
                filterMap.set(fb.tempName, fb);
            } else {
                filterMap.set(`${fb.filterId}`, fb); //`
            }
        }

        return filterMap;
    }

    public async doesFilterNameExist(filterName: string): Promise<boolean> {
        for (const [, fb] of await this.getFilterList()) {
            if (fb.filterName == filterName) {
                return true;
            }
        }

        return false;
    }

    /** Aktualizuje název filtračního bloku */
    protected async _updateFilterCaption(block: FilterBlock, index: number = 0): Promise<void> {
        if (!index) {
            const count = this._filters.filter(filter => StringUtils.isNullOrEmpty(filter.filterName));
            index = count.length;
        }

        block.tempName = `${(block.hasActiveAdvancedFilter ? await this._advancedFilterText : await this._filterText)} ${index}`;
    }

    /** Aktualizuje názvy filtračních bloků */
    protected async _updateFiltersCaption(): Promise<void> {
        this._filters.filter(filter => StringUtils.isNullOrEmpty(filter.filterName)).forEach((filter, index) => {
            this._updateFilterCaption(filter, index + 1);
        });

        /** Seřadí filtry podle názvů */
        this._filters.sort((a, b) => {
            if (!a.filterName && !b.filterName) {
                const indexA = this._getTempFilterIndex(a);
                const indexB = this._getTempFilterIndex(b);
                return indexA < indexB ? -1 : 1;
            }

            return (a.filterName ?? a.tempName).localeCompare(b.filterName ?? b.tempName);
        });
    }

    /**
     * Nacteni ulozenych filtru ze služby
     */
    public async loadSavedFilters(): Promise<void> {
        const filters = await getDataReaderClient().getDataRowsWithParams("UserProfile", ["ID", "Data", "IsDefault", "User"],
            ":UserProfile.ActualUserProfiles() AND FormName = @0 AND DataType = @1 AND Name = @2",
            { params: [this._formName, "FilterInfo", this._frameUniqueIdentifier], dataLength: -1 }, undefined, convertContextFromFrameToRequest(this.frameContext));

        for (const filter of filters) {
            const f = this.createNew(false);
            f.filterId = AttrValueReader.read(filter["ID"]);
            f.attrNameToDataType = this.attrNameToDataType;
            f.isImplementationFilter = AttrValueReader.read(filter["User"]) === 0;
            f.loadData(AttrValueReader.read(filter["Data"]));
        }

        await this._filterPanel.loadFilterItems();
    }

    /**
     * Nacte data filtru ze serveru. Pokud uz je filtr v seznamu je prepsan.
     * @param id ID Objektu
     */
    public async getSavedFilterById(id: string): Promise<FilterBlock> {
        const obj = await OWManager.getObject(id, "UserProfile", undefined, false);

        let exist = false;
        let f: FilterBlock;
        for (const fb of this._filters) {
            if (fb.filterId == id) {
                exist = true;
                f = fb;
            }
        }

        if (!exist) {
            f = this.createNew(false);
        }

        f.filterId = id;
        f.attrNameToDataType = this.attrNameToDataType;
        f.isImplementationFilter = await obj.getValue("User") instanceof NullOWObject;

        const data = await obj.getValue("Data") as string;

        f.loadData(data);

        if (!exist) {
            await this._filterPanel.loadFilterItems();
        }

        return f;
    }

    /**
     * Vytvoří nový prázdný filtrační blok
     * @param selected pokud se ma automaticky po vytvoření vybrat
     */
    public createNew(selected: boolean = true): FilterBlock {
        const fb = new FilterBlock();
        const binding = new FilterBlock();

        fb.objectName = this.className;
        binding.objectName = this.className;
        fb.currentUserId = this._currentUserId;
        binding.currentUserId = this._currentUserId;
        fb.frameContext = this.frameContext;
        binding.frameContext = this.frameContext;

        fb.formName = this._formName;
        fb.frameName = this._frameUniqueIdentifier;
        fb.frameCondition = this._frameCondition;
        fb.frameConditionParams = this._frameConditionParams;
        fb.disableValueShortening = this._disableValueShortening;
        binding.frameCondition = this._frameCondition;
        binding.frameConditionParams = this._frameConditionParams;
        binding.disableValueShortening = this._disableValueShortening;
        fb.binding(binding);
        fb.filterSaveStateChanged.add(() => {
            this.filterSaveStateChanged.trigger(this, fb);
        });

        this._filters.push(fb);
        if (selected) {
            this.selectFilter(fb);
        }

        this.filterCreated.trigger(this, fb);

        this.frameConditionChanged.add((sender, value) => {
            fb.frameCondition = value;
            fb.frameConditionChanged.trigger(sender, value);
            binding.frameCondition = value;
            binding.frameConditionChanged.trigger(sender, value);
        });

        if (selected) {
            fb.unsave();
        }

        return fb;
    }

    /**
     * Zobrazí vstup pro vložení dříve zkopírovaného filtru
     */
    public insertFilter(filter?: string): void {
        this.filterPanel.showInsertFilter(filter);
    }

    /** Zobrazí ve filtrovacím panelu vstup pro pokročilý filtr */
    public showAdvancedFilter(): void {
        if (!this.currentFilter) {
            this.createNew(true);

            this._appendToFilterPanel(this.currentFilter);
        }

        this.currentFilter.showAdvancedFilter();
    }

    private _createColumnItem(dgc: FilterManagerColumn): void {
        this.attrNameToDataType.set(dgc.attrName, {
            dataType: dgc.dataType,
            locKey: dgc.locKey,
            locParams: dgc.locParams,
            canFilterNull: dgc.canFilterNull,
            canFilterCompare: dgc.canFilterCompare,
            canFilterEqual: dgc.canFilterEqual,
            canFilterLike: dgc.canFilterLike,
        });

        const struct = FilterRegistrations.getDataTypeFilter(this.attrNameToDataType.get(dgc.attrName));
        if (struct == undefined) {
            return;
        }

        this.filterPanel.addColumnItem(dgc.locKey, async () => {
            // vytvoření filtrovaci skupiny pro sloupec
            const grp = new FilterGroup();
            grp.operator = this._filterSign;
            grp.attrName = dgc.attrName;
            grp.setLocKey(dgc.locKey, dgc.locParams);
            grp.filterStructure = struct;
            grp.displayFormat = dgc.displayFormat;
            grp.frameContext = this.frameContext;
            // pokud je filtr použit je vložen do stránky
            if (!this.currentFilter) {
                this._appendToFilterPanel(this.createNew());
                await this._filterPanel.loadFilterItems();
            }
            this.currentFilter.addGroup(grp);
            this._updateFilterConditionSignVisibility();
        });
    }

    /** Aktualizuje v panelu stav filtrů */
    public async updateFilterPanel(): Promise<void> {
        if (await this.filterPanel.updateFilterManager(this)) {
            const sortedColumns = this._columns.sort((first, second) => (first.locText.localeCompare(second.locText)));
            for (const column of sortedColumns) {
                this._createColumnItem(column);
            }

            this.filterPanel.registerSignButtonAction((sign: FilterJsonResultSign) => this._changeFilterConditionSign(sign));
        }

        // FilterBlock je vždy jako poslední, pokud je vložen a je vždy jen jeden pro frame
        const lastFilterBlock = this.filterPanel.currentFilterBlock;

        if (this.currentFilter) {
            if (lastFilterBlock instanceof FilterBlock) {
                if (this.currentFilter === lastFilterBlock) {
                    return; // Nic se nezměnilo, není tedy ani nic potřeba aktualizovat
                } else {
                    lastFilterBlock.remove();
                }
            }

            this._appendToFilterPanel(this.currentFilter);

            this._updateFilterConditionSignVisibility();
        } else if (lastFilterBlock instanceof FilterBlock) {
            lastFilterBlock.remove();
        }
    }

    /** Aktualizuje viditelnost bloku s tlačítky AND/OR */
    private _updateFilterConditionSignVisibility(): void {
        let groupUsed = 0;

        for (const filter of this._filters) {
            if (filter.isUsed) {
                const groups = filter.groups;

                for (const group of groups) {
                    if (group.enabled && !group.isRemoving) {
                        groupUsed++;
                    }

                    if (groupUsed > 1) {
                        this.filterPanel.updateOrAndButtonsState(group.operator === "or");

                        break;
                    }
                }
            }
        }

        this.filterPanel.updateConditionElementState(groupUsed > 1);
    }

    private _changeFilterConditionSign(sign: FilterJsonResultSign): void {
        this._filterSign = sign;
        for (const filter of this._filters) {
            if (filter.isUsed) {
                const groups = filter.groups;
                for (const group of groups) {
                    group.operator = sign;
                }
            }
        }
    }

    /**
     * vyčistí filtry (zruší vybrany filtr a nahraje prázdné pole)
     */
    public clearFilter(): void {
        if (this._lastUsedFilter) {
            this._lastUsedFilter.isUsed = false;
        }
        this._lastUsedFilter = undefined;
        this.updateFilterPanel();
        this.filterCleared.trigger(this, "");
    }

    public clearValueListFilters(): void {
        const fbs = this._filters.concat(this._filters.map(fb => fb.bindingFilterBlock));
        for (const fb of fbs) {
            for (const g of fb.groups) {
                for (let i = 0; i < g.items.length; i++) {
                    const operation = g.items[i].getResult(false, fb.type).operation;
                    if (operation === "value_is" || operation === "value_not_is") {
                        g.items[i].remove();
                        i--;
                    }
                }
            }
        }
    }
    /**
     * spustí filtrování
     */
    public execute(): void {
        this._updateFilterConditionSignVisibility();
        if (this.currentFilter) {
            this.currentFilter.execute();
        } else {
            this.clearFilter();
        }
    }

    /**
     * Trvale odstraní požadovaný filtr
     * @param filterBlock Požadovaný filtr
     */
    public async removeFilter(block: FilterBlock): Promise<void> {
        if (!this.canEditImplementationFilters && block.isImplementationFilter) {
            return;
        }

        if (block.filterId) {
            try {
                const obj = await OWManager.getObject(block.filterId, "UserProfile");
                await obj.delete();
            } finally {
                // Prazdne finally
            }
        }

        for (let i = 0; i < this._filters.length; i++) {
            if (this._filters[i] == block) {
                this._filters.splice(i, 1);
                break;
            }
        }

        if (this.currentFilter == block) {
            this.clearFilter();
        }

        block.remove();

        this._updateFilterConditionSignVisibility();

        if (this.currentFilter) {
            this._updateFilterCaption(this.currentFilter);
        }
    }

    /**
     * Vytvoří položku menu pro menu s filtry
     * @param filterBlock Blok filtrů
     * @param emptyBtn Tlačítko "Vše" pro zrušení jeho výběru po aktivaci uloženého filtru
     */
    public async createFilterMenuItem(filterBlock: FilterBlock, emptyBtn: DropDownMenuItem): Promise<DropDownMenuItemWithButton> {
        const buttons = FilterPanel.getCurrentButtons(filterBlock, this);
        const mitem = FilterPanel.createDropMenuItemForFilters("none", async () => {
            this.selectAndApplyFilter(filterBlock);
        }, "", true, buttons);

        mitem.setCaption(await this.getFilterCaption(filterBlock));

        if (filterBlock.isUsed) {
            mitem.icon = "checked";
            emptyBtn.icon = "none";
        } else {
            mitem.icon = "none";
        }

        filterBlock.addEventListener("filterSelected", (e: IBooleanDetailValueEvent) => {
            if (e.detail.value) {
                mitem.icon = "checked";
                emptyBtn.icon = "none";
            } else {
                mitem.icon = "none";
            }
        });

        return mitem;
    }

    /**
     * Vrací název filtru upravený podle jeho aktuálního stavu
     * @param block Blok filtru, který udržuje jeho stav
     */
    public async getFilterCaption(block: FilterBlock): Promise<string> {
        const modified = await Localization.getTextPromise("GUI-456326"); // upraveno
        let displayName = await this.getFilterDisplayName(block);

        if (!block.isSaved && block.filterName) {
            displayName += ` (${modified})`;
        }

        return displayName;
    }

    /**
     * Vrací název filtru upravený podle toho, jestli je implementační
     * @param block Blok filtru
     */
    public async getFilterDisplayName(block: FilterBlock): Promise<string> {
        let tempName;

        if (!block.filterName) {
            const index = this._getTempFilterIndex(block);
            await this._updateFilterCaption(block, index);
            tempName = this.getFilterTempName(block);
        }

        if (this.canEditImplementationFilters && block.isImplementationFilter) {
            const imp = await Localization.getTextPromise("GUI-740260"); // [IMP];
            if (block.filterName) {
                return `${block.filterName} ${imp}`;
            } else {
                logger.warn(`Filter with id: ${block.filterId} is missing a filterName.`);
                return `- ${imp}`;
            }
        }

        return block.filterName ?? tempName;
    }

    /**
     * Vrací číslo neuloženého filtru z jeho tempName
     * @param block Blok filtru
     */
    private _getTempFilterIndex(block: FilterBlock): number {
        const lastSpace = block.tempName.lastIndexOf(" ");
        const index = parseInt(block.tempName.slice(lastSpace + 1));
        return index;
    }

    /**
     * Vrací název neuloženého filtru
     * @param block Blok filtru
     */
    public async getFilterTempName(block: FilterBlock): Promise<string> {
        const unsaved = await Localization.getTextPromise("GUI-456328"); // Neuložený filtr
        return `${block.tempName} (${unsaved})`;
    }

    /**
    * Vytvoreni polozky radku filtru
    * @param attr - atribut filtru
    */
    private _createFilterItem(dataAttribute: IFilterJsonDataAttr): IFilterElementDetail {
        const column = this._columns.find((column) => StringUtils.compareCaseInsensitive(column.attrName, dataAttribute.attrName));
        const filterItem = document.createElement("span");
        filterItem.classList.add("filter-item");
        let isUsed = false;
        let tooltip = "";

        let conditionCount = 0;
        for (const condition of dataAttribute.conditions) {
            if (condition.enabled) {
                isUsed = true;
                conditionCount++;
                if (conditionCount > 1) {
                    const filterSign = document.createElement("span");
                    filterSign.classList.add("sign");
                    filterItem.appendChild(filterSign);
                    switch (condition.operator) {
                        case "or":
                            tooltip += "@GUI-452734 ";
                            Localization.getTextPromise("GUI-452734").then((locItem) => { // Nebo
                                filterSign.textContent = locItem;
                                filterItem.setAttribute("title", filterItem.getAttribute("title").replace("@GUI-452734", locItem.toLocaleLowerCase()));
                            }).catch((e) => logger.error(e));
                            break;
                        case "and":
                            tooltip += "@GUI-598905 ";
                            Localization.getTextPromise("GUI-598905").then((locItem) => { // A zároveň
                                filterSign.textContent = locItem;
                                filterItem.setAttribute("title", filterItem.getAttribute("title").replace("@GUI-598905", locItem.toLocaleLowerCase()));
                            }).catch((e) => logger.error(e));
                            break;
                    }
                }
                const filterCondition = document.createElement("span");
                filterCondition.classList.add("condition");
                filterItem.appendChild(filterCondition);

                if (!condition.isMultiItem) {
                    const operation = FilterRegistrations.getOperationByOperationId(column.dataType, condition.operation);
                    if (operation) {
                        tooltip += "@" + operation.lockey + " ";
                        Localization.getTextPromise(operation.lockey).then((locItem) => {
                            filterCondition.textContent = locItem;
                            filterItem.setAttribute("title", filterItem.getAttribute("title").replace("@" + operation.lockey, locItem.toLocaleLowerCase()));
                        }).catch((e) => logger.error(e));
                    }
                }

                const filterValue = document.createElement("span");
                filterValue.classList.add("value");
                filterItem.appendChild(filterValue);

                if (condition.values && condition.values.length > 0) {
                    for (const val of condition.values) {
                        if (val.userValue) {
                            filterValue.textContent += "\"" + val.userValue + "\" ";
                            tooltip += "\"" + val.userValue + "\" ";
                        } else {
                            if (val.value) {
                                switch (column.dataType) {
                                    case "ADate":
                                    case "Date":
                                        if (val.fieldType == "DateEdit") {
                                            const dateTime = DateUtils.dateToString(val.value).split(" ");
                                            filterValue.textContent += "\"" + dateTime[0] + "\" ";
                                            tooltip += "\"" + dateTime[0] + "\" ";
                                        } else {
                                            filterValue.textContent += "\"" + val.value + "\" ";
                                            tooltip += "\"" + val.value + "\" ";
                                        }
                                        break;
                                    case "ATime":
                                    case "Time":
                                        if (val.fieldType == "TimeEdit") {
                                            const dateTime = DateUtils.dateToString(val.value, dataAttribute.displayFormat).split(" ");
                                            filterValue.textContent += "\"" + dateTime[0] + "\" ";
                                            tooltip += "\"" + dateTime[0] + "\" ";
                                        } else {
                                            filterValue.textContent += "\"" + val.value + "\" ";
                                            tooltip += "\"" + val.value + "\" ";
                                        }
                                        break;
                                    case "DateTime":
                                        if (val.fieldType == "DateTimeEdit") {
                                            let formattedVal = DateUtils.getDateToStringFunction(dataAttribute.displayFormat).format(val.value);

                                            if (val.subOperationId == "day") {
                                                formattedVal = this._removeTime(formattedVal);
                                            }

                                            filterValue.textContent += "\"" + formattedVal + "\" ";
                                            tooltip += "\"" + formattedVal + "\" ";
                                        } else {
                                            filterValue.textContent += "\"" + val.value + "\" ";
                                            tooltip += "\"" + val.value + "\" ";
                                        }
                                        break;
                                    case "ABoolean":
                                    case "Boolean": {
                                        const operation = FilterRegistrations.getMultiOperationByOperationId(column.dataType, val.fieldId);
                                        if (operation) {
                                            Localization.getTextPromise(operation.lockey).then((locItem) => {
                                                filterCondition.textContent += locItem + " ";
                                                filterItem.setAttribute("title", filterCondition.innerText);
                                            }).catch((e) => logger.error(e));
                                        }
                                        break;
                                    }
                                    case "Flags": {
                                        let values: string = "";

                                        val.value.split(",").forEach((colorName: string) => {
                                            Flags.flags[colorName].getLocalizedName().then(text => {
                                                if (values.length > 1) {
                                                    values += ", ";
                                                }

                                                values += "\"" + text + "\"";

                                                filterValue.innerText = values;
                                                filterItem.setAttribute("title", filterValue.innerText);
                                            });
                                        });
                                        break;
                                    }
                                    default:
                                        filterValue.textContent += "\"" + val.value + "\" ";
                                        tooltip += "\"" + val.value + "\" ";
                                }
                            }
                        }
                    }
                }
                tooltip += " ";
            }
        }

        filterItem.setAttribute("title", tooltip);

        if (isUsed) {
            return { attrName: dataAttribute.attrName, filterElement: filterItem };
        } else {
            return null;
        }
    }

    private async _filterBlockExecuted(_sender: FilterBlock, filterBlockEventArgs: IFilterBlockExecutedEventArgs): Promise<void> {
        const filterElements: IFilterElementDetail[] = [];

        for (const attr of (filterBlockEventArgs.filterData as IFilterJsonData).attrs) {
            if (attr.enabled) {
                const newFilterElement = this._createFilterItem(attr);
                if (newFilterElement) {
                    filterElements.push(newFilterElement);
                }
            }
        }

        if (AppContext.instance.configuration.developmentMode) {
            logger.log("Filtering by: " + filterBlockEventArgs.condition);
        }

        this._updateFilterConditionSignVisibility();
        await this._updateFiltersCaption();

        this.filterExecuted.trigger(this, {
            condition: filterBlockEventArgs.condition,
            columns: filterBlockEventArgs.filteredAttrNames,
            filter: filterElements,
        });
    }

    /**
     * Připojí filtrovaci block do stránky
     * @param filterBlock
     */
    private _appendToFilterPanel(filterBlock: FilterBlock): void {
        this.filterPanel.showFilterBlock(filterBlock);

        this._assignFilterBlockEvents(filterBlock);
    }

    private _assignFilterBlockEvents(filterBlock: FilterBlock): void {
        if (!this._filterBlockExecutedHandler) {
            this._filterBlockExecutedHandler = (filterBlock: FilterBlock, filterBlockEventArgs: IFilterBlockExecutedEventArgs) => {
                return this._filterBlockExecuted(filterBlock, filterBlockEventArgs);
            };
        }

        if (!filterBlock.filterExecuted.containsListener(this._filterBlockExecutedHandler)) {
            filterBlock.filterExecuted.add(this._filterBlockExecutedHandler);
        }

        this.frameConditionChanged.add((sender, value) => {
            filterBlock.frameCondition = value;
            filterBlock.frameConditionChanged.trigger(sender, value);
        });
    }

    private _removeTime(date: string): string {
        let startOfTime = date.indexOf(":");

        if (startOfTime < 0) {
            return date;
        }

        while (date[startOfTime] != " " && date[startOfTime] != "T" && startOfTime > 0) {
            startOfTime--;
        }

        if (startOfTime > 0) {
            return date.substring(0, startOfTime);
        }
        return date;
    }

    public dispose(): void {
        this.filterExecuted.removeAll();
        this.filterCleared.removeAll();
        this.filterCreated.removeAll();
        this.filterChanged.removeAll();
        this.filterSaveStateChanged.removeAll();
        this.frameConditionChanged.removeAll();
        this.advancedConditionChanged.removeAll();

        this._filters?.forEach((filter) => filter.dispose());
        this._filters = [];
        this._lastUsedFilter?.dispose();
        this._columns = [];
        this._frameConditionParams = [];
        this.attrNameToDataType.clear();
    }
}

export interface IFilterExecutedEventArgs {
    condition: string;
    columns: Map<string, boolean> | null;
    filter: IFilterElementDetail[];
}

interface IFilterElementDetail {
    attrName: string;
    filterElement: HTMLSpanElement;
}
